import MojitoCore from 'mojito/core';
import { STORE_KEY, INITIAL_STATE } from './slice.js';

const reduxInstance = MojitoCore.Services.redux;

/**
 * Meta information selectors.
 *
 * @class MetaInformationSelectors
 * @name selectors
 * @memberof Mojito.Services.MetaInformation
 */

/**
 * Selects meta information state.
 *
 * @function selectState
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.MetaInformationState} The state of the meta information.
 *
 * @memberof Mojito.Services.MetaInformation.selectors
 */

export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || INITIAL_STATE;
};

/**
 * Select meta information.
 *
 * @function selectMetaInformation
 *
 * @param {string} type - Type of wanted meta information value.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {string} Meta information by type.
 *
 * @memberof Mojito.Services.MetaInformation.selectors
 */
export const selectMetaInformation = (type, state) => {
    return selectState(state)[type] || '0';
};

/**
 * Select total number of in-play events currently available for betting.
 *
 * @function selectLiveEventCount
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {number} Total number of in-play events.
 *
 * @memberof Mojito.Services.MetaInformation.selectors
 */
export const selectLiveEventCount = state => selectState(state).liveEventCount || 0;

/**
 * Subscribes to the changes of a particular meta information state property.
 * Intended to be used by external consumers (e.g. DBX/Portal) to observe the live event count.
 *
 * @function subscribe
 *
 * @type {Mojito.Core.Services.redux.SubscriberFunction}
 * @memberof Mojito.Services.MetaInformation.selectors
 */
export const subscribe = reduxInstance.getSubscriber(STORE_KEY);
